import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { apiClient, type MeditationReminder } from '../services/api';
import './SettingsPage.css'; // .toggle-switch
import './MeditationReminderCard.css';

const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];

/**
 * Напоминание о медитации на главной (Sofa 24.09): время ЧЧ:ММ + дни недели.
 * Пуш «после первой практики» ведёт сюда с ?reminder=HH:MM — карточка подсвечивается и предзаполняется.
 */
export const MeditationReminderCard: React.FC = () => {
  const t = useTranslation();
  const [params, setParams] = useSearchParams();
  const ref = useRef<HTMLDivElement>(null);
  const [saved, setSaved] = useState<MeditationReminder | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [time, setTime] = useState('21:00');
  const [days, setDays] = useState<number[]>(ALL_DAYS);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [highlight, setHighlight] = useState(false);

  useEffect(() => {
    const suggested = params.get('reminder');
    apiClient.getMeditationReminder().then((r) => {
      setSaved(r);
      setEnabled(r.enabled || !!suggested);
      setTime(r.time || (suggested && /^\d{2}:\d{2}$/.test(suggested) ? suggested : '21:00'));
      setDays(r.days.length ? r.days : ALL_DAYS);
    }).catch(() => undefined);
    if (suggested !== null) {
      setHighlight(true);
      setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
      params.delete('reminder');
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = !saved || enabled !== saved.enabled
    || (enabled && (time !== saved.time || days.join(',') !== saved.days.join(',')));

  const toggleDay = (d: number) => {
    setDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort()));
    setStatus('idle');
  };

  const save = async (nextEnabled = enabled) => {
    setSaving(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const r = await apiClient.updateMeditationReminder({ enabled: nextEnabled && days.length > 0, time, days, timezone: tz });
      setSaved(r);
      setEnabled(r.enabled);
      setStatus('saved');
      setHighlight(false);
    } catch {
      setStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const onToggle = (v: boolean) => {
    setEnabled(v);
    setStatus('idle');
    if (!v && saved?.enabled) void save(false); // выключение — сразу
  };

  return (
    <div ref={ref} className={`reminder-card${highlight ? ' reminder-card--highlight' : ''}`}>
      <div className="reminder-card__head">
        <div>
          <h2 className="reminder-card__title">{t.reminder.title}</h2>
          <p className="reminder-card__subtitle">{t.reminder.subtitle}</p>
        </div>
        <label className="toggle-switch">
          <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} />
          <span className="toggle-slider" />
        </label>
      </div>

      {enabled && (
        <div className="reminder-card__body">
          <label className="reminder-card__time">
            <span>{t.reminder.time}</span>
            <input type="time" value={time} onChange={(e) => { setTime(e.target.value); setStatus('idle'); }} />
          </label>
          <div className="reminder-card__days">
            {ALL_DAYS.map((d) => (
              <button
                key={d}
                type="button"
                className={`reminder-day${days.includes(d) ? ' reminder-day--on' : ''}`}
                onClick={() => toggleDay(d)}
              >
                {t.reminder.days[d - 1]}
              </button>
            ))}
          </div>
          {days.length === 0 && <p className="reminder-card__hint">{t.reminder.pickDay}</p>}
          <button
            type="button"
            className="reminder-card__save"
            disabled={saving || !dirty || days.length === 0 || !time}
            onClick={() => void save()}
          >
            {saving ? t.reminder.saving : t.reminder.save}
          </button>
        </div>
      )}
      {status === 'saved' && (
        <p className="reminder-card__ok">{enabled ? t.reminder.savedOn.replace('{time}', time) : t.reminder.savedOff}</p>
      )}
      {status === 'error' && <p className="reminder-card__err">{t.reminder.error}</p>}
    </div>
  );
};
